import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { useContext, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';
import { withTheme } from 'styled-components';

import { getTariffsTOUCosts } from '../../../api/Optimise';
import { AppContext } from '../../../context/AppContext';
import { useRedirectIfNoTariffs } from '../../../hooks';
import { RippleLoaderLines, TariffRateTable } from '../../../ui';
import { StyledH3, StyledKeylineDiv } from '../../../ui/Styled';
import { processTOUCostsToRates } from '../../../utils/tariffRateUtils';

import { AdditionalDetails, AnnualBill, FAQs, TariffDetailsChart } from './TariffDetails';

const TariffDetailsContainer = ({ theme }) => {
  const history = useHistory();
  const [tariffCombination, setTariffCombination] = useState(null);
  const [loadingImportTariff, setLoadingImportTariff] = useState(true);
  const [importTariff, setImportTariff] = useState(null);
  const [loadingExportTariff, setLoadingExportTariff] = useState(true);
  const [exportTariff, setExportTariff] = useState(null);
  const [tariffRates, setTariffRates] = useState(null);
  const { combinationId } = useParams();
  const [processingCosts, setProcessingCosts] = useState(true);

  const { recommendedTariffs } = useContext(AppContext);

  useRedirectIfNoTariffs();

  useEffect(() => {
    if (!!recommendedTariffs) {
      recommendedTariffs.forEach((tariff) => {
        if (tariff.tariff_combination.tariff_standard_combination_id === combinationId) {
          setTariffCombination(tariff);
        }
      });
    }
  }, [recommendedTariffs, combinationId]);

  // Fetch and process 12 months of TOU cost data to show rate variability
  useEffect(() => {
    if (!!tariffCombination) {
      const fetchTariffRates = async () => {
        try {
          const importTariffId = tariffCombination.tariff_combination.import_tariff_id;
          const exportTariffId = tariffCombination.tariff_combination.export_tariff_id;

          // Fetch 12 months (365 days) of TOU cost data and process it
          const rates = await processTOUCostsToRates(importTariffId, exportTariffId, 365);
          setTariffRates(rates);
        } catch (error) {
          console.error('Error fetching tariff rates:', error);
          setTariffRates(null);
        } finally {
          setProcessingCosts(false);
        }
      };

      fetchTariffRates();
    }
  }, [tariffCombination]);

  useEffect(() => {
    if (!!tariffCombination) {
      const end = moment.utc().startOf('day');
      const endTimestampUTC = end.unix();

      const start = end.subtract(1, 'day');
      const startTimestampUTC = start.unix();

      setLoadingImportTariff(true);

      getTariffsTOUCosts(startTimestampUTC, endTimestampUTC, tariffCombination.tariff_combination.import_tariff_id)
        .then((touCosts) => {
          const data = touCosts.cost_windows.map((window) => ({
            x: moment.unix(window.start_timestamp_utc).utc().format('HH:mm'),
            y: window.rate,
          }));
          // We add an extra data point for better spacing on the chart
          if (data.length) {
            data.push({
              x: '24:00',
              y: data[data.length - 1].y,
            });
          }
          return setImportTariff(data);
        })
        .finally(() => {
          setLoadingImportTariff(false);
        });
    }
  }, [tariffCombination]);

  useEffect(() => {
    if (!!tariffCombination) {
      const end = moment.utc().startOf('day');
      const endTimestampUTC = end.unix();

      const start = end.subtract(1, 'day');
      const startTimestampUTC = start.unix();

      setLoadingExportTariff(true);

      getTariffsTOUCosts(startTimestampUTC, endTimestampUTC, tariffCombination.tariff_combination.export_tariff_id)
        .then((touCosts) => {
          const data = touCosts.cost_windows.map((window) => ({
            x: moment.unix(window.start_timestamp_utc).utc().format('HH:mm'),
            y: window.rate,
          }));
          data.push({
            x: '24:00',
            y: data[data.length - 1].y,
          });
          return setExportTariff(data);
        })
        .finally(() => {
          setLoadingExportTariff(false);
        });
    }
  }, [tariffCombination]);

  return (
    <>
      <div className="pt-4">
        <div className="pb-3">
          <a className="text-decoration-none font-weight-bold" href="/app/tariffs/recommended">
            <FontAwesomeIcon icon={faChevronLeft} color={theme.colors.primary} /> Back
          </a>
        </div>
        <StyledH3 className="pb-1">Tariff Details</StyledH3>
        <StyledKeylineDiv>
          {/* <p>Variable Import with Fixed Flat Export</p> */}
          {!tariffCombination?.tariff_combination ? (
            <RippleLoaderLines lines={2} />
          ) : (
            <>
              <p>Import tariff: {tariffCombination?.tariff_combination?.import_tariff_name}</p>
              <p>Export tariff: {tariffCombination?.tariff_combination?.export_tariff_name}</p>
            </>
          )}
          <StyledKeylineDiv>
            {/* <p>
              This is an import and export tariff, optimised to give you the
              best rates for consuming and selling your energy during peak
              periods.
            </p> */}
            <TariffDetailsChart
              loadingImportTariff={loadingImportTariff}
              loadingExportTariff={loadingExportTariff}
              importTariff={importTariff}
              exportTariff={exportTariff}
            />
          </StyledKeylineDiv>
          <p className="mt-3">
            Some tariffs have a variable rate, so over the course of a year, the daily peak and cheap rates will
            fluctuate. For the above tariff, the variability shown across the previous 12 months is shown below:
          </p>
          <div className="pt-2">
            <TariffRateTable rates={tariffRates} loading={processingCosts} keyLine />
          </div>
          <StyledH3 className="pb-1 pt-4">Your potential annual bill on this tariff:</StyledH3>
          <AnnualBill tariffCombination={tariffCombination} />
          <StyledH3 className="pb-1 pt-4">Additional tariff details:</StyledH3>
          <AdditionalDetails tariffCombination={tariffCombination} />
          <FAQs />
        </StyledKeylineDiv>
      </div>
      <Button onClick={() => (window.location.href = theme.links.howDoISwitch)} className="mb-0" block>
        How do I switch?
      </Button>
      <Button onClick={() => history.push('/app/tariffs/recommended')} variant="secondary" block>
        Back to recommended tariffs
      </Button>
    </>
  );
};

export default withTheme(TariffDetailsContainer);
